/**
 * Single-file project info view. Mirrors `renderInfo` from ts/src/info.ts
 * but emits semantic HTML instead of text, and reads the filename from
 * the File object rather than a path string (the Node-isolated
 * `basename` call is the one part of `renderInfo` that's not browser-
 * clean).
 *
 * Fields match the CLI:
 *   FL Studio version · tempo · time-sig · PPQ
 *   Title / Artists / Genre (if present)
 *   Channels: N (by kind)
 *   Patterns: N
 *   Mixer: N active inserts, M effect slots
 *   Arrangements: N (T tracks, C clips)
 *   Plugins: top-N + "and M more"
 *   Samples: top-N + "and M more"
 */

import type { FLPProject, Channel, MixerInsert } from "flpdiff";
import { removeA, removeB } from "./state.ts";
import { h, formatBytes, clamp } from "./ui.ts";

const TOP_N = 8;

export function renderProjectInfo(
  project: FLPProject,
  filename: string,
  fileBytes: number,
  label: string,
): HTMLElement {
  const md = project.metadata;
  const tempo = getTempoOrZero(project);
  const channelCounts = countChannelsByKind(project.channels);
  const active = activeInserts(project.inserts);
  const slots = effectSlotCount(project.inserts);
  const plugins = collectPluginNames(project);
  const samples = collectSamplePaths(project).map((p) => {
    const idx = Math.max(p.lastIndexOf("/"), p.lastIndexOf("\\"));
    return idx < 0 ? p : p.substring(idx + 1);
  });
  const tracksTotal = project.arrangements.reduce(
    (n, a) => n + a.tracks.length,
    0,
  );
  const clipsTotal = project.arrangements.reduce(
    (n, a) => n + a.clips.length,
    0,
  );

  const metaLine = [
    `FL Studio ${fmtVersion(md.version)}`,
    `${tempo.toFixed(1)} BPM`,
    fmtTimeSig(md.timeSignatureNumerator, md.timeSignatureDenominator),
    `PPQ ${project.header.ppq}`,
  ].join(" · ");

  const rows: Array<[string, string]> = [];
  if (md.title) rows.push(["Title", md.title]);
  if (md.artists) rows.push(["Artists", md.artists]);
  if (md.genre) rows.push(["Genre", md.genre]);
  rows.push(["Channels", fmtChannelKinds(channelCounts)]);
  rows.push(["Patterns", String(project.patterns.length)]);
  rows.push(["Mixer", `${active.length} active inserts, ${slots} effect slots`]);
  rows.push([
    "Arrangements",
    `${project.arrangements.length} (${tracksTotal} tracks, ${clipsTotal} clips)`,
  ]);
  rows.push(["Plugins", fmtList(plugins, TOP_N)]);
  rows.push(["Samples", fmtList(samples, TOP_N)]);

  const dl = h("dl", { class: "info__grid" });
  for (const [k, v] of rows) {
    dl.appendChild(h("dt", null, k));
    dl.appendChild(h("dd", null, clamp(v)));
  }

  const onRemove = label === "before" ? removeA : removeB;
  const removeBtn = h("button", {
    type: "button",
    class: "info__remove",
    "aria-label": `Remove ${label} file`,
    onclick: onRemove,
  }, "×");

  return h("section", { class: "info", "data-label": label }, [
    h("header", { class: "info__header" }, [
      h("span", { class: "info__tag" }, label),
      h("span", { class: "info__name" }, filename),
      h("span", { class: "info__size" }, formatBytes(fileBytes)),
      removeBtn,
    ]),
    h("p", { class: "info__meta" }, metaLine),
    dl,
  ]);
}

// ---- helpers (ported from ts/src/info.ts) ----

function getTempoOrZero(project: FLPProject): number {
  const modern = project.events.find(
    (e) => e.kind === "u32" && e.opcode === 0x9c,
  );
  if (modern && modern.kind === "u32") return modern.value / 1000;
  const coarse = project.events.find(
    (e) => e.kind === "u16" && e.opcode === 0x42,
  );
  if (!coarse || coarse.kind !== "u16") return 0;
  let tempo = coarse.value;
  const fine = project.events.find(
    (e) => e.kind === "u16" && e.opcode === 0x5d,
  );
  if (fine && fine.kind === "u16") tempo += fine.value / 1000;
  return tempo;
}

function countChannelsByKind(channels: readonly Channel[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const ch of channels) counts[ch.kind] = (counts[ch.kind] ?? 0) + 1;
  return counts;
}

function activeInserts(inserts: readonly MixerInsert[]): MixerInsert[] {
  return inserts.filter(
    (ins) => ins.slots.length > 0 || ins.name !== undefined,
  );
}

function effectSlotCount(inserts: readonly MixerInsert[]): number {
  let n = 0;
  for (const ins of inserts) {
    for (const s of ins.slots) {
      if (s.hasPlugin === true || s.pluginName) n++;
    }
  }
  return n;
}

function collectPluginNames(project: FLPProject): string[] {
  const seen = new Map<string, null>();
  for (const ch of project.channels) {
    const p = ch.plugin;
    if (p) {
      const name = p.name ?? p.internalName;
      if (name) seen.set(name, null);
    }
  }
  for (const ins of project.inserts) {
    for (const s of ins.slots) {
      const name = s.pluginVstName ?? s.pluginName ?? s.internalName;
      if (name) seen.set(name, null);
    }
  }
  return Array.from(seen.keys());
}

function collectSamplePaths(project: FLPProject): string[] {
  const seen = new Map<string, null>();
  for (const ch of project.channels) {
    if (ch.sample_path !== undefined && ch.sample_path !== "") {
      seen.set(ch.sample_path, null);
    }
  }
  return Array.from(seen.keys());
}

function fmtTimeSig(
  num: number | undefined,
  denom: number | undefined,
): string {
  if (num === undefined || denom === undefined) return "?";
  return `${num}/${denom}`;
}

function fmtChannelKinds(counts: Record<string, number>): string {
  const keys = Object.keys(counts);
  if (keys.length === 0) return "0";
  const total = keys.reduce((n, k) => n + counts[k]!, 0);
  const sortedKeys = [...keys].sort();
  const parts = sortedKeys.map((k) => {
    const n = counts[k]!;
    return `${n} ${k}${n !== 1 ? "s" : ""}`;
  });
  return `${total} (${parts.join(", ")})`;
}

function fmtList(values: readonly string[], maxItems: number): string {
  if (values.length === 0) return "(none)";
  if (values.length <= maxItems) return values.join(", ");
  const shown = values.slice(0, maxItems).join(", ");
  return `${shown}, … and ${values.length - maxItems} more`;
}

function fmtVersion(
  v:
    | { major: number; minor: number; patch: number; build: number | null }
    | undefined,
): string {
  if (v === undefined) return "?";
  const parts = [v.major, v.minor, v.patch];
  if (v.build !== null) parts.push(v.build);
  return parts.join(".");
}
